<template>
  <div class="column-mapping-container">
    <div class="container py-4">
      <!-- Progress Header -->
      <div class="step-header mb-4">
        <h2 class="text-center">Step 4: Map Your Data Columns</h2>
        <p class="text-center text-muted">
          Match your Excel columns to the required MSDAT data fields
        </p>
      </div>

      <!-- Alert Messages -->
      <b-alert v-if="!hasUploadedData" show variant="warning">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        No data found. Please go back and upload your Excel file first.
      </b-alert>

      <div v-else class="row">
        <!-- Left Column: Column Mapping Form -->
        <div class="col-lg-6">
          <b-card class="mapping-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-columns mr-2 text-primary"></i>
                <strong>Column Mappings</strong>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="ml-auto"
                  @click="autoDetect"
                >
                  <i class="fas fa-magic mr-1"></i>
                  Auto-Detect
                </b-button>
              </div>
            </template>

            <!-- Required Fields -->
            <h6 class="text-primary mb-3">
              <i class="fas fa-asterisk mr-1"></i>
              Required Fields
            </h6>

            <b-form-group
              v-for="field in requiredFields"
              :key="field.name"
              :label="field.label"
              :description="field.description"
              class="mb-3"
            >
              <b-form-select
                v-model="columnMapping[field.name]"
                :options="columnOptions"
                :state="getFieldState(field.name)"
                @change="onMappingChange"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    -- Select a column --
                  </b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback>
                This field is required
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Optional Fields -->
            <hr class="my-4" />
            <h6 class="text-secondary mb-3">
              <i class="fas fa-plus-circle mr-1"></i>
              Optional Fields
            </h6>

            <b-form-group
              v-for="field in optionalFields"
              :key="field.name"
              :label="field.label"
              :description="field.description"
              class="mb-3"
            >
              <b-form-select
                v-model="columnMapping[field.name]"
                :options="columnOptions"
                @change="onMappingChange"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    -- Not mapped --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-card>
        </div>

        <!-- Right Column: Validation & Preview -->
        <div class="col-lg-6">
          <!-- Validation Status -->
          <b-card class="validation-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-check-circle mr-2" :class="validationIconClass"></i>
                <strong>Validation Status</strong>
              </div>
            </template>

            <div v-if="validationResult">
              <!-- Errors -->
              <div v-if="validationResult.errors.length > 0" class="mb-3">
                <h6 class="text-danger">
                  <i class="fas fa-times-circle mr-1"></i>
                  Errors ({{ validationResult.errors.length }})
                </h6>
                <ul class="error-list">
                  <li v-for="(error, idx) in validationResult.errors" :key="'err-' + idx">
                    {{ error }}
                  </li>
                </ul>
              </div>

              <!-- Warnings -->
              <div v-if="validationResult.warnings.length > 0" class="mb-3">
                <h6 class="text-warning">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  Warnings ({{ validationResult.warnings.length }})
                </h6>
                <ul class="warning-list">
                  <li v-for="(warn, idx) in validationResult.warnings" :key="'warn-' + idx">
                    {{ warn }}
                  </li>
                </ul>
              </div>

              <!-- Success -->
              <div v-if="validationResult.isValid" class="text-success">
                <i class="fas fa-check-circle mr-2"></i>
                All required columns are mapped correctly!
              </div>
            </div>

            <div v-else class="text-muted">
              <i class="fas fa-info-circle mr-2"></i>
              Map all required columns to validate your data
            </div>
          </b-card>

          <!-- Data Summary -->
          <b-card v-if="dataSummary" class="summary-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-chart-bar mr-2 text-info"></i>
                <strong>Data Summary</strong>
              </div>
            </template>

            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-value">{{ dataSummary.totalRows }}</span>
                <span class="summary-label">Total Rows</span>
              </div>
              <div class="summary-item">
                <span class="summary-value">{{ dataSummary.indicatorCount }}</span>
                <span class="summary-label">Indicators</span>
              </div>
              <div class="summary-item">
                <span class="summary-value">{{ dataSummary.locationCount }}</span>
                <span class="summary-label">Locations</span>
              </div>
              <div class="summary-item">
                <span class="summary-value">{{ dataSummary.periodCount }}</span>
                <span class="summary-label">Periods</span>
              </div>
              <div class="summary-item">
                <span class="summary-value">{{ dataSummary.datasourceCount }}</span>
                <span class="summary-label">Data Sources</span>
              </div>
              <div class="summary-item">
                <span class="summary-value">
                  {{ dataSummary.valueRange.min.toFixed(1) }} - {{ dataSummary.valueRange.max.toFixed(1) }}
                </span>
                <span class="summary-label">Value Range</span>
              </div>
            </div>
          </b-card>

          <!-- Data Preview Table -->
          <b-card class="preview-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-table mr-2 text-secondary"></i>
                <strong>Transformed Data Preview</strong>
                <span class="ml-auto text-muted small">
                  Showing first {{ Math.min(5, transformedData.length) }} rows
                </span>
              </div>
            </template>

            <div v-if="transformedData.length > 0" class="table-responsive">
              <table class="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Indicator</th>
                    <th>Location</th>
                    <th>Period</th>
                    <th>Value</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in previewRows" :key="idx">
                    <td>{{ row.indicator }}</td>
                    <td>{{ row.location }}</td>
                    <td>{{ row.period }}</td>
                    <td>{{ row.value }}</td>
                    <td>{{ row.datasource }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-muted py-4">
              <i class="fas fa-table fa-2x mb-2"></i>
              <p>Map required columns to see transformed data preview</p>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="navigation-buttons mt-4 d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>
          Back to Data Upload
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canProceed"
          @click="saveAndContinue"
        >
          Continue to Appearance
          <i class="fas fa-arrow-right ml-2"></i>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import {
  extractColumns,
  autoDetectColumnMapping,
  validateDataSchema,
  transformToMSDATFormat,
  generateDataSummary,
  REQUIRED_COLUMNS,
} from '../../utils/dataTransformer';

export default {
  name: 'ColumnMapping',

  data() {
    return {
      columnMapping: {
        indicator: null,
        location: null,
        period: null,
        value: null,
        datasource: null,
        value_type: null,
        gender: null,
        age_group: null,
        category: null,
      },
      hasInitializedFromConfig: false,
      validationResult: null,
      transformedData: [],
      dataSummary: null,
      requiredFields: [
        {
          name: 'indicator',
          label: 'Indicator Column',
          description: 'The column containing indicator names or IDs',
        },
        {
          name: 'location',
          label: 'Location Column',
          description: 'The column containing location/state/LGA names',
        },
        {
          name: 'period',
          label: 'Period/Year Column',
          description: 'The column containing time period or year',
        },
        {
          name: 'value',
          label: 'Value Column',
          description: 'The column containing numeric data values',
        },
        {
          name: 'datasource',
          label: 'Data Source Column',
          description: 'The column containing data source names',
        },
      ],
      optionalFields: [
        {
          name: 'value_type',
          label: 'Value Type Column',
          description: 'The column containing value type (e.g., percentage, count)',
        },
        {
          name: 'gender',
          label: 'Gender Column',
          description: 'The column containing gender disaggregation',
        },
        {
          name: 'age_group',
          label: 'Age Group Column',
          description: 'The column containing age group disaggregation',
        },
        {
          name: 'category',
          label: 'Category Column',
          description: 'The column containing additional categorization',
        },
      ],
    };
  },

  computed: {
    ...mapState('configurer', ['rawExcelData', 'selectedSheet', 'columnMappingConfig']),

    hasUploadedData() {
      return this.rawExcelData && Object.keys(this.rawExcelData).length > 0;
    },

    currentSheetData() {
      if (!this.hasUploadedData || !this.selectedSheet) {
        return [];
      }
      return this.rawExcelData[this.selectedSheet] || [];
    },

    availableColumns() {
      return extractColumns(this.currentSheetData);
    },

    columnOptions() {
      return this.availableColumns.map((col) => ({
        value: col,
        text: col,
      }));
    },

    canProceed() {
      return this.validationResult && this.validationResult.isValid;
    },

    previewRows() {
      return this.transformedData.slice(0, 5);
    },

    validationIconClass() {
      if (!this.validationResult) return 'text-muted';
      if (this.validationResult.isValid) return 'text-success';
      return 'text-danger';
    },
  },

  watch: {
    currentSheetData: {
      immediate: true,
      handler() {
        if (this.currentSheetData.length > 0) {
          this.initializeMapping();

          if (this.columnMappingConfig && !this.hasInitializedFromConfig) {
            this.hasInitializedFromConfig = true;
            const filteredConfig = {};
            Object.keys(this.columnMappingConfig).forEach((field) => {
              const mappedCol = this.columnMappingConfig[field];
              // STRICT CHECK: Only restore mappings if the column actually exists in the current sheet
              if (mappedCol && this.availableColumns.includes(mappedCol)) {
                filteredConfig[field] = mappedCol;
              } else {
                filteredConfig[field] = null;
              }
            });
            this.columnMapping = { ...this.columnMapping, ...filteredConfig };
            this.onMappingChange();
          } else {
            // Auto detect for new sheets / no config
            this.autoDetect();
          }
        }
      },
    },
  },

  mounted() {
    // Handled powerfully in watcher now
  },

  methods: {
    ...mapMutations('configurer', [
      'SET_COLUMN_MAPPING',
      'SET_TRANSFORMED_DATA',
      'SET_DATA_SUMMARY',
    ]),

    initializeMapping() {
      // Reset mapping when sheet data changes
      Object.keys(this.columnMapping).forEach((key) => {
        this.columnMapping[key] = null;
      });
    },

    autoDetect() {
      if (this.availableColumns.length === 0) return;

      const detected = autoDetectColumnMapping(this.availableColumns);
      this.columnMapping = { ...this.columnMapping, ...detected };
      this.onMappingChange();

      this.$bvToast.toast('Columns auto-detected based on common naming patterns', {
        title: 'Auto-Detection Complete',
        variant: 'info',
        solid: true,
      });
    },

    onMappingChange() {
      // Validate the mapping
      this.validationResult = validateDataSchema(this.currentSheetData, this.columnMapping);

      // Transform data if valid
      if (this.validationResult.isValid) {
        this.transformedData = transformToMSDATFormat(this.currentSheetData, this.columnMapping);
        this.dataSummary = generateDataSummary(this.transformedData);
      } else {
        this.transformedData = [];
        this.dataSummary = null;
      }
    },

    getFieldState(fieldName) {
      if (!this.validationResult) return null;
      const isMapped = this.columnMapping[fieldName] !== null;
      if (REQUIRED_COLUMNS.includes(fieldName)) {
        return !!isMapped;
      }
      return null;
    },

    saveAndContinue() {
      if (!this.canProceed) return;

      // Save to store
      this.SET_COLUMN_MAPPING(this.columnMapping);
      this.SET_TRANSFORMED_DATA(this.transformedData);
      this.SET_DATA_SUMMARY(this.dataSummary);

      // Navigate to next step
      this.$router.push({ name: 'configure-appearance' });
    },

    goBack() {
      this.$router.push({ name: 'configure-data' });
    },
  },
};
</script>

<style scoped>
.column-mapping-container {
  min-height: 100vh;
  /* background-color: #f8f9fa; Removed */
  padding-bottom: 2rem;
}

.step-header h2 {
  color: #2c3e50;
  font-weight: 700;
}

/* Glass Cards */
.mapping-card,
.validation-card,
.summary-card,
.preview-card {
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  margin-bottom: 24px;
}

::v-deep .card-header {
  background-color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border-radius: 20px 20px 0 0 !important;
}

/* Form Styles */
::v-deep .custom-select {
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    height: 48px;
    box-shadow: none;
}

::v-deep .custom-select:focus {
    background: #ffffff;
    border-color: #007D53;
    box-shadow: 0 0 0 3px rgba(0, 125, 83, 0.1);
}

.text-primary { color: #007D53 !important; }
.btn-outline-primary {
    color: #007D53;
    border-color: #007D53;
}
.btn-outline-primary:hover {
    background-color: #007D53;
    color: white;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.summary-item {
  text-align: center;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
}

.summary-item:hover {
    transform: translateY(-2px);
    background-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.summary-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.summary-label {
  display: block;
  font-size: 0.75rem;
  color: #636e72;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

/* Table Styles */
.table-responsive {
  max-height: 250px;
  overflow-y: auto;
  border-radius: 12px;
}

::v-deep .table {
    background: transparent;
}

::v-deep .table thead th {
    background-color: rgba(0, 125, 83, 0.1);
    color: #007D53;
    border: none;
    position: sticky;
    top: 0;
}

::v-deep .table td {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    vertical-align: middle;
}

/* Navigation */
.navigation-buttons {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background-color: rgba(255, 255, 255, 0.5);
  margin-top: 2rem;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(10px);
}

::v-deep .btn-primary {
  background-color: #007D53;
  border-color: #007D53;
  border-radius: 10px;
  padding: 0.5rem 1.5rem;
}

::v-deep .btn-outline-secondary {
    border-radius: 10px;
    border-color: #b2bec3;
    color: #636e72;
}
</style>
